/**
 * lectureApi.js
 * -------------
 * Real REST API client for Education > Lectures module.
 * Routes directly to FastAPI backend /api/lectures adhering to module_implementation_standards.md.
 */
import api from '../../../../api/client'

/**
 * Fetch paginated lectures from backend with dynamic filter parameters.
 */
export async function listLectures(params = {}) {
  const query = {}

  if (params.academicYear && params.academicYear !== 'All') {
    query.academicYear = params.academicYear
  }
  if (params.companyId && params.companyId !== 'All') {
    query.companyId = params.companyId
  }
  if (params.branchId && params.branchId !== 'All') {
    query.branchId = params.branchId
  }
  if (params.courseId && params.courseId !== 'All') {
    query.courseId = params.courseId
  }
  if (params.batchId && params.batchId !== 'All') {
    query.batchId = params.batchId
  }
  if (params.subjectId && params.subjectId !== 'All') {
    query.subjectId = params.subjectId
  }
  if (params.facultyId && params.facultyId !== 'All') {
    query.facultyId = params.facultyId
  }
  if (params.fromDate) {
    query.fromDate = params.fromDate
  }
  if (params.toDate) {
    query.toDate = params.toDate
  }
  if (params.status && params.status !== 'All') {
    query.status = params.status
  }
  if (params.category && params.category !== 'All') {
    query.category = params.category
  }
  if (params.searchField) {
    query.searchField = params.searchField
  }
  if (params.searchText && params.searchText.trim()) {
    query.searchText = params.searchText.trim()
  }
  if (params.orderBy !== undefined) {
    query.orderBy = params.orderBy
  }
  if (params.page) {
    query.page = params.page
  }
  if (params.pageSize) {
    query.pageSize = params.pageSize
  }

  const { data } = await api.get('/lectures', { params: query })
  return data
}

/**
 * Fetch single lecture details by ID.
 */
export async function getLectureById(id) {
  const { data } = await api.get(`/lectures/${id}`)
  return data
}

/**
 * Toggle cancellation status of a lecture (cancel or restore).
 */
export async function toggleCancelLecture(id) {
  const { data } = await api.post(`/lectures/${id}/cancel`)
  return data
}

/**
 * Soft delete a lecture record.
 */
export async function deleteLecture(id) {
  const { data } = await api.delete(`/lectures/${id}`)
  return data
}

/**
 * Create a new lecture record (used by entry screen).
 */
export async function createLecture(payload) {
  const { data } = await api.post('/lectures', payload)
  return data
}

/**
 * Update an existing lecture record (used by entry screen).
 */
export async function updateLecture(id, payload) {
  const { data } = await api.put(`/lectures/${id}`, payload)
  return data
}

/**
 * Retrieve student attendance roster for a specific lecture.
 */
export async function getLectureAttendance(lectureId) {
  const { data } = await api.get(`/lectures/${lectureId}/attendance`)
  return data
}

/**
 * Save student attendance marks for a specific lecture.
 */
export async function saveLectureAttendance(lectureId, records) {
  const { data } = await api.post(`/lectures/${lectureId}/attendance`, { records })
  return data
}

/**
 * Fetch predefined report dataset for Education > Lectures.
 */
export async function fetchReportData(reportKey, params = {}) {
  const query = {}

  if (params.academicYear && params.academicYear !== 'All') {
    query.academicYear = params.academicYear
  }
  if (params.companyId && params.companyId !== 'All') {
    query.companyId = params.companyId
  }
  if (params.branchId && params.branchId !== 'All') {
    query.branchId = params.branchId
  }
  if (params.courseId && params.courseId !== 'All') {
    query.courseId = params.courseId
  }
  if (params.batchId && params.batchId !== 'All') {
    query.batchId = params.batchId
  }
  if (params.subjectId && params.subjectId !== 'All') {
    query.subjectId = params.subjectId
  }
  if (params.facultyId && params.facultyId !== 'All') {
    query.facultyId = params.facultyId
  }
  if (params.fromDate) {
    query.fromDate = params.fromDate
  }
  if (params.toDate) {
    query.toDate = params.toDate
  }
  if (params.status && params.status !== 'All') {
    query.status = params.status
  }
  if (params.category && params.category !== 'All') {
    query.category = params.category
  }
  if (params.searchField) {
    query.searchField = params.searchField
  }
  if (params.searchText && params.searchText.trim()) {
    query.searchText = params.searchText.trim()
  }

  const { data } = await api.get(`/lectures/reports/${reportKey}`, { params: query })
  return data
}

/**
 * Download sample Excel template for Lecture bulk upload.
 */
export async function downloadLectureTemplate() {
  const response = await api.get('/lectures/template', {
    responseType: 'blob',
  })
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'Import_Lectures_Template.xlsx'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

/**
 * Bulk upload Excel spreadsheet for Lectures.
 * @param {File} file - Excel spreadsheet file (.xlsx / .xls)
 */
export async function uploadLectureExcel(file) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post('/lectures/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// Backward-compatibility export for legacy/unmodernized modules
export const LECTURE_BRANCHES = []
